import { useEffect, type ReactNode } from "react";
import { Button, FieldCheckbox } from "@knowtex-ai/components";
import { Attachment, Microphone, Square, Xmark } from "iconoir-react";
import {
  PROMPT_WORKFLOW_INSTRUCTION,
  PROMPT_WORKFLOW_STEPS,
} from "../../noteList/SmartAssistPromptTools";
import { announce } from "../../../lib/announce";
import { ACCEPTED_FILE_TYPES, formatAttachmentSize, getUploadedFileKey } from "../core/attachments";
import type { SmartAssistQuickAction, UseSmartAssistResult } from "../core/types";
import SmartAssistQuickPrompts from "./SmartAssistQuickPrompts";

const SMART_ASSIST_PLACEHOLDER =
  "Write your smart assist question or command here. You can also insert a dot phrase.";

type SmartAssistFooterProps = Pick<
  UseSmartAssistResult,
  | "canUploadFiles"
  | "prompt"
  | "promptRef"
  | "handlePromptChange"
  | "handlePromptSelect"
  | "handlePromptKeyDown"
  | "promptError"
  | "promptWorkflowStep"
  | "activeQuickAction"
  | "selectQuickAction"
  | "startCreatePrompt"
  | "includeConsentDisclaimer"
  | "setIncludeConsentDisclaimer"
  | "isAwaitingResponse"
  | "submit"
  | "stop"
  | "isDictating"
  | "isDictationStarting"
  | "dictationStatus"
  | "dictationError"
  | "toggleDictation"
  | "uploadedFiles"
  | "attachmentError"
  | "fileInputRef"
  | "handleFileSelection"
  | "removeUploadedFile"
> & {
  quickPrompts?: SmartAssistQuickAction[];
  showCreatePrompt?: boolean;
  /** Surface-specific controls rendered under the composer. */
  footerActions?: ReactNode;
};

function InlineAlert({
  children,
  alignRight = false,
}: {
  children: ReactNode;
  alignRight?: boolean;
}) {
  return (
    <span
      role="alert"
      className={`min-w-0 break-words text-xs font-semibold text-red-600 ${
        alignRight ? "text-right" : ""
      }`}
    >
      {children}
    </span>
  );
}

export default function SmartAssistFooter({
  canUploadFiles,
  prompt,
  promptRef,
  handlePromptChange,
  handlePromptSelect,
  handlePromptKeyDown,
  promptError,
  promptWorkflowStep,
  activeQuickAction,
  selectQuickAction,
  startCreatePrompt,
  includeConsentDisclaimer,
  setIncludeConsentDisclaimer,
  isAwaitingResponse,
  submit,
  stop,
  isDictating,
  isDictationStarting,
  dictationStatus,
  dictationError,
  toggleDictation,
  uploadedFiles,
  attachmentError,
  fileInputRef,
  handleFileSelection,
  removeUploadedFile,
  quickPrompts,
  showCreatePrompt,
  footerActions,
}: SmartAssistFooterProps) {
  const isComposerLocked = isAwaitingResponse || isDictating || isDictationStarting;

  useEffect(() => {
    if (dictationStatus) announce(dictationStatus);
  }, [dictationStatus]);
  const placeholder = activeQuickAction
    ? activeQuickAction.instruction
    : promptWorkflowStep === PROMPT_WORKFLOW_STEPS.AWAITING_EXAMPLE
      ? PROMPT_WORKFLOW_INSTRUCTION
      : SMART_ASSIST_PLACEHOLDER;

  return (
    <div className="shrink-0 border-t border-slate-100 bg-white px-4 pb-3 pt-2">
      <div
        data-testid="smart-assist-composer"
        className="mx-auto w-full min-w-0 max-w-3xl rounded-2xl border border-slate-300 bg-white p-2 shadow-[0_4px_18px_rgba(15,23,42,0.10)] focus-within:border-accent1 focus-within:ring-1 focus-within:ring-accent1"
      >
        {canUploadFiles && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_TYPES}
            className="hidden"
            onChange={handleFileSelection}
            disabled={isAwaitingResponse || isDictationStarting}
          />
        )}
        {uploadedFiles.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2 px-1 pt-1">
            {uploadedFiles.map((file) => (
              <div
                key={getUploadedFileKey(file)}
                className="flex max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-accent1"
              >
                <div className="min-w-0">
                  <div className="max-w-[14rem] truncate font-semibold">{file.filename}</div>
                  <div className="text-[11px] text-light-grey">
                    {formatAttachmentSize(file.sizeBytes)}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="text"
                  size="small"
                  icon={Xmark}
                  aria-label={`Remove ${file.filename}`}
                  onClick={() => removeUploadedFile(file)}
                  disabled={isComposerLocked}
                />
              </div>
            ))}
          </div>
        )}
        <SmartAssistQuickPrompts
          disabled={isComposerLocked}
          onSelect={selectQuickAction}
          onCreatePrompt={startCreatePrompt}
          actions={quickPrompts}
          showCreatePrompt={showCreatePrompt}
        />
        <textarea
          ref={promptRef}
          aria-label="Smart Assist prompt editor"
          value={prompt}
          onChange={handlePromptChange}
          onSelect={handlePromptSelect}
          onKeyDown={handlePromptKeyDown}
          placeholder={placeholder}
          rows={4}
          className="box-border max-h-[50vh] min-h-[6rem] min-w-0 w-full max-w-full resize-y border-0 bg-transparent px-2 py-2 text-sm text-accent1 outline-none focus:ring-0"
          disabled={isAwaitingResponse}
          readOnly={isDictationStarting}
        />
        {activeQuickAction?.offersConsentToggle && (
          <div className="px-2 pb-2">
            {/* The library's label wrapper doesn't name its span-based checkbox. */}
            <FieldCheckbox
              aria-label="Include consent statement"
              size="small"
              checked={includeConsentDisclaimer}
              onCheckedChange={setIncludeConsentDisclaimer}
              disabled={isComposerLocked}
            >
              Include consent statement
            </FieldCheckbox>
          </div>
        )}
        <div
          data-testid="smart-assist-composer-controls"
          className="flex flex-wrap items-end justify-between gap-3 px-1 pb-1"
        >
          <div className="flex max-w-full flex-wrap items-center gap-2">
            {canUploadFiles && (
              <Button
                type="button"
                variant="text"
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={isComposerLocked}
                icon={Attachment}
                aria-label="Upload PDFs/images/files"
                title="Attach files"
              />
            )}
            {attachmentError && <InlineAlert>{attachmentError}</InlineAlert>}
            {dictationError && <InlineAlert>{dictationError}</InlineAlert>}
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={toggleDictation}
              disabled={isAwaitingResponse || isDictationStarting}
              icon={isDictating ? Square : Microphone}
              className="shrink-0"
              aria-label={
                isDictationStarting
                  ? "Starting Smart Assist dictation"
                  : isDictating
                    ? "Stop Smart Assist dictation"
                    : "Start Smart Assist dictation"
              }
            >
              {isDictationStarting ? "Starting..." : isDictating ? "Stop dictation" : "Dictate"}
            </Button>
            {dictationStatus && <span className="text-xs text-light-grey">{dictationStatus}</span>}
          </div>
          <div
            data-testid="smart-assist-submit-controls"
            className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2"
          >
            {promptError && <InlineAlert alignRight>{promptError}</InlineAlert>}
            {isAwaitingResponse ? (
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={stop}
                icon={Square}
                aria-label="Stop generating Smart Assist response"
              >
                Stop
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="small"
                onClick={submit}
                disabled={isDictating || isDictationStarting}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </div>
      {footerActions && (
        <div className="mx-auto mt-2 flex w-full min-w-0 max-w-3xl flex-wrap items-center justify-between gap-3">
          {footerActions}
        </div>
      )}
      <p className="mb-0 mt-2 text-center text-[11px] text-light-grey">
        Smart Assist responses are drafts. Review before using them in the note.
      </p>
    </div>
  );
}
